import React, { ComponentPropsWithoutRef } from 'react';
import { LayoutAnimation } from 'react-native';
import { MBText } from '@shared/components/MBText';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { useValidateUploads } from '@shared/scenes/upload/hooks';
import { Colors } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { MBAttachmentButton } from '../containers/attachmentButton';
import { MBAttachmentList } from '../containers/attachmentList';
import { MBChatInputPlusButton } from '../containers/plusButton';
import { MBChatInputSendButton } from '../containers/sendButton';
import { MBEmojiButton } from './emojiButton';
import { Styles } from './styles';

export interface MBChatInputBarProps {
  draftId: string;
  value: string;
  isEditing: boolean;
  hasAttachments: boolean;
  placeholder?: string;
  hideUrgent?: boolean;
  autoFocus?: boolean;
  innerRef?: React.RefObject<MBAutosizeTextInput>;
  onBlur?(): void;
  onFocus?(): void;
  onChangeText(text: string): void;
  onCursorPositionChange?(position: number): void;
  onPasteFiles(files: File[]): void;
  onSend(): void;
  onSubmitEditing(): void;
  onCancelEdit(): void;
  onAddEmoji(emoji: string): void;
}

interface MBChatInputBarState {
  sending: boolean;
}

export class MBChatInputBar extends React.PureComponent<MBChatInputBarProps, MBChatInputBarState> {
  state: MBChatInputBarState = { sending: false };

  componentDidUpdate(prevProps: MBChatInputBarProps) {
    const canSend = this.canSend();
    const couldSend = !!prevProps.value || prevProps.hasAttachments;
    if (canSend !== couldSend) {
      if (!!LayoutAnimation) {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      }
    }
  }

  canSend = () => !!this.props.value.length || this.props.hasAttachments;
  send = async () => {
    if (!this.canSend()) {
      return;
    }

    this.setState({ sending: true });
    try {
      await this.props.onSend();
    } finally {
      this.setState({ sending: false });
    }
  };

  /*
        When pressing enter on web: submit editing
        (will select current mention or send the mssage)
        When pressing the keyboard button on native: don't do anything
        (this requires pressing the actual send button)
    */
  onSubmitEditing = () => {
    this.props.onSubmitEditing();
  };

  change = (text: string) => {
    if (this.state.sending) {
      return;
    }
    this.props.onChangeText(text);
  };

  render() {
    const {
      value,
      draftId,
      placeholder,
      hideUrgent,
      isEditing,
      autoFocus,
      onAddEmoji,
      onPasteFiles,
      onCursorPositionChange,
      onBlur,
      onFocus,
    } = this.props;
    const { sending } = this.state;
    return (
      <MBView paddingVertical={0.625} style={Styles.container}>
        <View row shrinkWrap fullWidth style={{ minHeight: 42, alignItems: 'flex-end' }}>
          <View style={{ justifyContent: 'flex-end', flexGrow: 0, flexShrink: 0 }}>
            {!isEditing && <MBPlusButtonWrapped draftId={draftId} hideUrgent={hideUrgent} />}
            {isEditing && <View paddingLeft={1} />}
          </View>

          <MBView flex style={Styles.inputContainer}>
            <MBAttachmentList draftId={draftId} />
            <View row>
              <MBAutosizeTextInput
                style={{ flex: 1, paddingVertical: 4 }}
                ref={this.props.innerRef}
                placeholder={placeholder}
                size="normal"
                autoFocus={autoFocus}
                padding="normal"
                value={value}
                onChangeText={this.change}
                maxRows={10}
                onPasteFiles={onPasteFiles}
                onSubmitEditing={this.onSubmitEditing}
                onCursorPositionChange={onCursorPositionChange}
                onBlur={onBlur}
                onFocus={onFocus}
              />
              <MBEmojiButton onSelect={onAddEmoji} />
            </View>
          </MBView>

          <View style={{ justifyContent: 'flex-end', flexGrow: 0, flexShrink: 0 }}>
            {!!sending && (
              <MBLoadingIndicator
                style={[Styles.iconButton, Styles.alignBottom]}
                color={Colors.v2.purple}
              />
            )}
            {!sending && this.canSend() && (
              <MBChatInputSendButton
                draftId={draftId}
                style={Styles.alignBottom}
                onPress={this.send}
                testID="chat-send-button"
              />
            )}
            {!isEditing && !sending && !this.canSend() && (
              <MBAttachmentButtonWrapped draftId={draftId} />
            )}
          </View>
        </View>

        {isEditing && (
          <MBView row wrap style={Styles.cancelButton}>
            <MBTouchableOpacity onPress={this.props.onCancelEdit} style={{ paddingHorizontal: 5 }}>
              <MBText>{i18n('common.cancel')}</MBText>
            </MBTouchableOpacity>
          </MBView>
        )}
      </MBView>
    );
  }
}

function MBAttachmentButtonWrapped({
  draftId,
}: Omit<ComponentPropsWithoutRef<typeof MBAttachmentButton>, 'validateUploads'>) {
  const validateUploads = useValidateUploads({ showUpgradeModal: true });

  return <MBAttachmentButton draftId={draftId} validateUploads={validateUploads} />;
}

function MBPlusButtonWrapped({
  draftId,
  hideUrgent,
}: Omit<ComponentPropsWithoutRef<typeof MBChatInputPlusButton>, 'validateUploads'>) {
  const validateUploads = useValidateUploads({ showUpgradeModal: true });

  return (
    <MBChatInputPlusButton
      draftId={draftId}
      hideUrgent={hideUrgent}
      validateUploads={validateUploads}
    />
  );
}
